import { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import type { AuthSession } from "@/types/user";
import {
  accountToSession,
  getAccessToken,
  signInWithMicrosoft,
  signOut as signOutMsal
} from "@services/auth";
import { setTokenProvider } from "@services/api";

/**
 * Authentication hook wrapping MSAL.
 *
 * Exposes:
 *   - session: the current AuthSession or null
 *   - signIn(email?): triggers Microsoft popup with optional email login_hint
 *   - signOut(): MSAL logout + clear local cache
 *   - isAuthenticated: convenience boolean
 *
 * Wires the API client's token provider on mount so backend calls
 * automatically attach the Bearer token.
 */
export function useAuth() {
  const { accounts } = useMsal();
  const [session, setSession] = useState<AuthSession | null>(null);

  // Keep session in sync with MSAL's account list
  useEffect(() => {
    if (accounts.length > 0) {
      setSession(accountToSession(accounts[0]));
    } else {
      setSession(null);
    }
  }, [accounts]);

  // Wire the API client's auth header
  useEffect(() => {
    setTokenProvider(getAccessToken);
  }, []);

  const signIn = useCallback(async (email?: string) => {
    const newSession = await signInWithMicrosoft(email);
    if (newSession) setSession(newSession);
    return newSession;
  }, []);

  const signOut = useCallback(async () => {
    await signOutMsal();
    setSession(null);
  }, []);

  return { session, signIn, signOut, isAuthenticated: session !== null };
}
